import { RazorpayKeyId } from "./constants";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export const resolveRazorpayKey = (orderPayload) => {
  const order = orderPayload?.data || orderPayload || {};
  return (
    order.key_id ||
    order.keyId ||
    order.key ||
    order.razorpayKey ||
    orderPayload?.key_id ||
    orderPayload?.key ||
    RazorpayKeyId ||
    ""
  );
};

export const resolveRazorpayOrder = (orderPayload) => {
  const order = orderPayload?.data || orderPayload || {};
  return {
    id: order.id || order.orderId || order.order_id || "",
    amount: Number(order.amount) || 0,
    currency: order.currency || "INR",
    receipt: order.receipt || "",
    status: order.status || "",
    raw: order,
  };
};

/**
 * Opens Razorpay checkout and resolves with payment response on success.
 * @returns {Promise<{razorpay_payment_id:string, razorpay_order_id:string, razorpay_signature:string}>}
 */
export const openRazorpayCheckout = async ({
  orderPayload,
  amountInRupees,
  user,
  description = "Diagnostic test booking",
  notes = {},
}) => {
  const loaded = await loadRazorpayScript();
  if (!loaded || !window.Razorpay) {
    throw new Error("Unable to load Razorpay. Please check your connection.");
  }

  const order = resolveRazorpayOrder(orderPayload);
  const key = resolveRazorpayKey(orderPayload);

  if (!key) {
    throw new Error("Razorpay key is missing.");
  }
  if (!order.id) {
    throw new Error("Razorpay order was not created. Please try again.");
  }

  const amountPaise =
    order.amount > 0
      ? order.amount
      : Math.round(Number(amountInRupees || 0) * 100);

  if (!amountPaise || amountPaise < 100) {
    throw new Error("Invalid payment amount.");
  }

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key,
      amount: amountPaise,
      currency: order.currency,
      name: "Kior Healthcare",
      description,
      image: "https://www.kiorhealthcare.com/kior_img.png",
      order_id: order.id,
      prefill: {
        name: [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "",
        email: user?.email || "",
        contact: user?.mobileNumber || "",
      },
      notes,
      theme: {
        color: "#10519e",
      },
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled")),
        confirm_close: true,
      },
      handler: (response) => {
        if (!response?.razorpay_payment_id) {
          reject(new Error("Payment completed but payment id is missing."));
          return;
        }
        resolve(response);
      },
    });

    rzp.on("payment.failed", (response) => {
      const message =
        response?.error?.description ||
        response?.error?.reason ||
        "Payment failed. Please try again.";
      reject(new Error(message));
    });

    rzp.open();
  });
};
